// Module for handling store changes related to transitions between screens. At time of writing, all of the actions
// in the module are higher-level thunks that dispatch sub-actions. No store structure is needed. So it
// departs from the "Ducks" pattern a tiny bit.

// constants
import { drawingStates, scriptConfigViews } from 'constants/values';
import { overlayLayers } from 'constants/imagery';
import { scriptMethods, scriptTypes } from 'constants/scripts';
import { resourceType } from 'constants/loading';

// Imported ducks
import { endPendingResourcesOfType } from 'loading';
import { resetManualScriptParams } from 'manual';
import { clearMessages } from 'messages';
import { setIsFullScreen } from 'settings';
import { resetProductInputs } from 'current/uiwidgets/product-inputs';
import { resetPlantingPlanParams } from 'planting-plan-form';
import {
  setActiveCropId,
  setActiveOverlayLayer,
  setActiveScriptMethod,
  setActiveScriptType
} from 'current';
import { removeScripts, revertZoneHistoryToSaved } from 'scripts';
import { setDrawingState } from 'current/uiwidgets/drawing';
import {
  setActiveScriptConfigView,
  setActiveProductRateLegend,
  setScriptOptionsIsOpen,
  setSelectedOverlayLayerGroup
} from 'current/uiwidgets';

// finders
import { resetSriptFlow } from 'script-flow';

// selectors
import { getSupportedCropIdsForAdvancedScripting } from 'crops/selectors';
import { getActiveScriptType } from 'current/selectors';
import { isScriptFlowActive } from 'script-flow/selectors';

// util
import { determineStartingView } from 'util/env';

export const initAdvancedUi = () => {
  return (dispatch) => {
    dispatch(setIsFullScreen(true));
    dispatch(setActiveScriptMethod(scriptMethods.ADVANCED));
    dispatch(setActiveScriptType(scriptTypes.PLANTING));
  };
};

export const initManualScriptUi = (scriptType) => {
  return (dispatch, getState) => {
    const state = getState();
    scriptType = scriptType || getActiveScriptType(state);

    dispatch(setActiveScriptType(scriptType));
    dispatch(setIsFullScreen(true));
    dispatch(setActiveScriptMethod(scriptMethods.MANUAL));
  };
};

export const initExperimentScriptUi = () => {
  return (dispatch) => {

    dispatch(setActiveScriptType(scriptTypes.EXPERIMENT));
    dispatch(setIsFullScreen(true));
    dispatch(setActiveScriptMethod(scriptMethods.MANUAL));
  };
};

export const cleanupCreateUi = () => {
  return (dispatch) => {
    dispatch(resetPlantingPlanParams());
    dispatch(resetManualScriptParams());
    dispatch(resetProductInputs());

    dispatch(endPendingResourcesOfType({ type: resourceType.ADVANCED_SCRIPT_AVAILABILITY }));
    dispatch(endPendingResourcesOfType({ type: resourceType.ADVANCED_SCRIPT_RECOMMENDATIONS }));
  };
};

export const initCreateScriptUi = () => {
  return (dispatch, getState) => {
    const state = getState();
    const [cropId] = getSupportedCropIdsForAdvancedScripting(); // only one for now

    dispatch(setIsFullScreen(true));
    // if we've gotten this far and there's still no active script type, it hasn't been set by
    // ios and we're coming straight to the route from desktop, just default to fertility
    if (!getActiveScriptType(state)) {
      dispatch(setActiveScriptType(determineStartingView({}) || scriptTypes.FERTILITY));
    }
    dispatch(setActiveCropId(cropId));
  };
};

export const initEditScriptUi = (scriptId) => {
  return (dispatch) => {

    // set seed rate as default layer
    dispatch(setActiveOverlayLayer(overlayLayers.PRODUCT_RATE));
    dispatch(setActiveProductRateLegend());

    // set fullscreen
    dispatch(setIsFullScreen(true));

    // drawing-related UI
    dispatch(setDrawingState(drawingStates.NONE));

    // clear any messages. (Unlikely a message should carry over from one screen to another.)
    dispatch(clearMessages());

    // close script options menu
    dispatch(setScriptOptionsIsOpen({ scriptId, status: false }));
    // set script config view to summary so zone list displays
    dispatch(setActiveScriptConfigView(scriptConfigViews.SUMMARY));
  };
};

export const initManageScriptsUi = () => {
  return (dispatch) => {
    dispatch(setIsFullScreen(false));
    // clear any messages. (Unlikely a message should carry over from one screen to another.)
    dispatch(clearMessages());
    dispatch(resetSriptFlow());
  };
};

export const cleanupEditScriptUi = (scriptId) => {
  return (dispatch, getState) => {
    const state = getState();

    if (isScriptFlowActive(state)) {
      // The user was creating a new script, and canceled
      dispatch(removeScripts([scriptId]));
    } else {
      // The user was editing a saved script, and canceled
      dispatch(revertZoneHistoryToSaved(scriptId));
    }
    dispatch(resetProductInputs());
    dispatch(setSelectedOverlayLayerGroup());
  };
};
